import React, { useEffect, useState } from 'react';
import { ui } from '../ui';
import {
  WeeklyMetrics, RosterMember, CoverageSlot,
  getTimeTrackingWeeklyMetrics, getTimeTrackingRoster, getTimeTrackingCoverage,
} from '../api';
import { TimeTrackingUploads } from './TimeTrackingUploads';
import { TimeTrackingExceptions } from './TimeTrackingExceptions';

// Staffing-reduction gates from the CS Cost Reduction handoff, hours/week:
// baseline 478 (Jan–Jun 2026 average), Milestone 1 ≤ 430 (from Aug 3, 2026),
// Dec 2026 target ≈ 335. The Workforce tables are loaded by the CS project's
// Python loaders; this tab is a read-only view over them.
const BASELINE_HOURS = 478;
const MILESTONE1_HOURS = 430;
const DEC_TARGET_HOURS = 335;

const fmtWeek = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const fmtHours = (h: number) => h.toFixed(2);
const fmtPct = (p: number) => `${p > 0 ? '+' : ''}${(p * 100).toFixed(1)}%`;

// ── Coverage grid helpers ─────────────────────────────────────────────────────
// Date-only arithmetic in UTC-midnight space so the viewer's timezone can't shift a day.
const etTodayIso = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
const addDaysIso = (iso: string, n: number) =>
  new Date(new Date(`${iso}T00:00:00Z`).getTime() + n * 86_400_000).toISOString().slice(0, 10);
// The Sunday of the ET week containing `iso`.
const sundayOf = (iso: string) => addDaysIso(iso, -new Date(`${iso}T00:00:00Z`).getUTCDay());
const fmtGridDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: 'UTC', month: 'short', day: 'numeric' });
// "13:45" → "1:45p"
const fmtSlot = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')}${h < 12 ? 'a' : 'p'}`;
};
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Chevron for the collapsible section headers.
const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <span className="inline-block w-4 text-muted" aria-hidden="true">{open ? '▾' : '▸'}</span>
);

// Worked-hours cell tone: green once Milestone 1 is met, amber below baseline, red above.
const workedTone = (h: number) =>
  h <= MILESTONE1_HOURS ? 'text-green-600' : h <= BASELINE_HOURS ? 'text-amber-600' : 'text-red-600';

export const TimeTrackingAdmin: React.FC = () => {
  const [weeks, setWeeks] = useState<WeeklyMetrics[]>([]);
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Coverage grid week (Sunday ISO date, ET). Defaults to the current ET week.
  const [coverageWeek, setCoverageWeek] = useState(() => sundayOf(etTodayIso()));
  const [coverage, setCoverage] = useState<CoverageSlot[]>([]);

  // Per-section collapse state; everything starts expanded.
  const [collapsed, setCollapsed] = useState<Record<'uploads' | 'exceptions' | 'coverage' | 'hours' | 'roster', boolean>>({
    uploads: false, exceptions: false, coverage: false, hours: false, roster: false,
  });
  const toggle = (key: 'uploads' | 'exceptions' | 'coverage' | 'hours' | 'roster') =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const load = () =>
    Promise.all([getTimeTrackingWeeklyMetrics(), getTimeTrackingRoster()])
      .then(([w, r]) => { setWeeks(w); setRoster(r); });

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    getTimeTrackingCoverage(coverageWeek).then(setCoverage);
  }, [coverageWeek]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([load(), getTimeTrackingCoverage(coverageWeek).then(setCoverage)]);
    } finally { setRefreshing(false); }
  };

  const activeRoster = roster.filter(r => r.IsActive);

  // Pivot the flat slot list into rows (interval) x columns (day 0–6), trimmed to
  // intervals where the week has any required/scheduled/actual value at all.
  const slotsByInterval = new Map<string, CoverageSlot[]>();
  coverage.forEach(s => {
    const row = slotsByInterval.get(s.IntervalStartET) ?? new Array(7).fill(null);
    row[s.DayOffset] = s;
    slotsByInterval.set(s.IntervalStartET, row);
  });
  const activeIntervals = Array.from(slotsByInterval.entries())
    .filter(([, row]) => row.some((s: CoverageSlot | null) =>
      s && (s.RequiredAgents > 0 || s.ScheduledAgents > 0 || s.ActualAgents > 0)))
    .map(([interval]) => interval)
    .sort();
  // Only color/compare Actual on days that have any punch data (ADP lags; future days have none).
  const dayHasActual = Array.from({ length: 7 }, (_, d) =>
    coverage.some(s => s.DayOffset === d && s.ActualAgents > 0));

  // Cell tone tracks Scheduled vs Required — the "moving toward the desired schedule"
  // signal. Over-scheduling by 2+ agents is flagged too (paying for excess coverage).
  const cellTone = (s: CoverageSlot) => {
    if (s.RequiredAgents === 0) return '';
    if (s.ScheduledAgents < s.RequiredAgents) return 'bg-red-500/10';
    if (s.ScheduledAgents >= s.RequiredAgents + 2) return 'bg-orange-500/20';
    return 'bg-green-500/10';
  };
  const actualTone = (s: CoverageSlot, hasActual: boolean) => {
    if (!hasActual || s.RequiredAgents === 0) return 'text-muted';
    return s.ActualAgents >= s.RequiredAgents ? 'text-green-600' : 'text-red-600';
  };
  // Non-answering (Supervisor/OtherProject) count shown as a muted "(N)" suffix —
  // informational only, never rendered when N is 0 so it doesn't clutter every cell.
  const OtherCount: React.FC<{ n: number }> = ({ n }) =>
    n > 0 ? <span className="text-muted"> ({n})</span> : null;

  return (
    <div className="space-y-6">
      {/* ── Data Uploads (MakeShift schedule + ADP payroll) ──── */}
      <section className={ui.sectionCard}>
        <button type="button" className="mb-1 flex items-center gap-2" onClick={() => toggle('uploads')}>
          <Chevron open={!collapsed.uploads} />
          <h2 className={ui.sectionTitle}>Data Uploads</h2>
        </button>
        {!collapsed.uploads && (
          <div className="mt-3">
            <TimeTrackingUploads onUploaded={handleRefresh} />
          </div>
        )}
      </section>

      {/* ── Off-Call Exceptions (recurring blocks that reduce ScheduledAgents) ── */}
      <section className={ui.sectionCard}>
        <button type="button" className="mb-1 flex items-center gap-2" onClick={() => toggle('exceptions')}>
          <Chevron open={!collapsed.exceptions} />
          <h2 className={ui.sectionTitle}>Off-Call Exceptions</h2>
        </button>
        {!collapsed.exceptions && (
          <div className="mt-3">
            <TimeTrackingExceptions roster={activeRoster} onChanged={handleRefresh} />
          </div>
        )}
      </section>

      {/* ── Coverage grid (required vs scheduled vs actual) ──── */}
      <section className={ui.sectionCard}>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
          <button type="button" className="flex items-center gap-2" onClick={() => toggle('coverage')}>
            <Chevron open={!collapsed.coverage} />
            <h2 className={ui.sectionTitle}>Coverage — Required vs Scheduled vs Actual</h2>
          </button>
          <div className="flex items-center gap-2">
            <button className={ui.btnSecondary} onClick={() => setCoverageWeek(addDaysIso(coverageWeek, -7))}>
              ← Prev
            </button>
            <span className="min-w-[11rem] text-center text-sm font-medium text-secondary">
              Week of {fmtGridDate(coverageWeek)} – {fmtGridDate(addDaysIso(coverageWeek, 6))}
            </span>
            <button className={ui.btnSecondary} onClick={() => setCoverageWeek(addDaysIso(coverageWeek, 7))}>
              Next →
            </button>
            {coverageWeek !== sundayOf(etTodayIso()) && (
              <button className={ui.btnSecondary} onClick={() => setCoverageWeek(sundayOf(etTodayIso()))}>
                Today
              </button>
            )}
          </div>
        </div>
        {!collapsed.coverage && (<>
        <p className="mb-4 text-sm text-muted">
          Each cell: <span className="font-medium text-secondary">Required · Scheduled · Actual</span> agents
          per 15-minute interval (ET).{' '}
          <span className="rounded bg-green-500/10 px-1">green</span> = scheduled meets required,{' '}
          <span className="rounded bg-red-500/10 px-1">red</span> = under-scheduled,{' '}
          <span className="rounded bg-orange-500/20 px-1">orange</span> = over-scheduled by 2 or more agents.
          Actual is dimmed until ADP timecards for that day are loaded.
          Scheduled/Actual count Answering-role agents only; a muted{' '}
          <span className="text-muted">(n)</span> after either number is the non-answering
          staff (Supervisors/Other Project) or an Answering-role agent excluded for that
          interval by an active Off-Call Exception (see below) — informational only, not
          included in the coloring or counts.
        </p>
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>Time (ET)</th>
                {DAY_NAMES.map((name, d) => (
                  <th key={name} className={`${ui.th} text-center`}>
                    {name} <span className="font-normal text-muted">{fmtGridDate(addDaysIso(coverageWeek, d))}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={ui.tbody}>
              {activeIntervals.map(interval => {
                const row = slotsByInterval.get(interval)!;
                return (
                  <tr key={interval}>
                    <td className={`${ui.td} whitespace-nowrap font-medium`}>{fmtSlot(interval)}</td>
                    {row.map((s: CoverageSlot | null, d: number) => (
                      <td key={d} className={`${ui.td} text-center tabular-nums ${s ? cellTone(s) : ''}`}>
                        {s ? (
                          <>
                            <span className="text-muted">{s.RequiredAgents}</span>
                            <span className="text-muted"> · </span>
                            <span className={s.RequiredAgents > 0 && s.ScheduledAgents < s.RequiredAgents ? 'font-semibold text-red-600' : 'font-semibold'}>
                              {s.ScheduledAgents}
                            </span>
                            <OtherCount n={s.ScheduledOtherAgents} />
                            <span className="text-muted"> · </span>
                            <span className={actualTone(s, dayHasActual[d])}>
                              {dayHasActual[d] ? s.ActualAgents : '—'}
                            </span>
                            {dayHasActual[d] && <OtherCount n={s.ActualOtherAgents} />}
                          </>
                        ) : ''}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {activeIntervals.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-muted">
                    No coverage data for this week — schedules, punches, and the staffing model are loaded by the CS project.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>)}
      </section>

      {/* ── Weekly hours ─────────────────────────────────────── */}
      <section className={ui.sectionCard}>
        <div className="mb-1 flex items-center justify-between gap-3">
          <button type="button" className="flex items-center gap-2" onClick={() => toggle('hours')}>
            <Chevron open={!collapsed.hours} />
            <h2 className={ui.sectionTitle}>Weekly Hours</h2>
          </button>
          <button className={ui.btnSecondary} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {!collapsed.hours && (<>
        <p className="mb-4 text-sm text-muted">
          Sunday-start weeks from MakeShift schedules and ADP timecards. Targets (worked hrs/wk):{' '}
          baseline <span className="font-medium text-secondary">{BASELINE_HOURS}</span> ·{' '}
          Milestone 1 <span className="font-medium text-green-600">≤ {MILESTONE1_HOURS}</span> ·{' '}
          Dec 2026 <span className="font-medium text-secondary">≈ {DEC_TARGET_HOURS}</span>.
        </p>
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>Week (Sun–Sat)</th>
                <th className={`${ui.th} text-right`}>Scheduled</th>
                <th className={`${ui.th} text-right`}>Worked</th>
                <th className={`${ui.th} text-right`}>Answering-role</th>
                <th className={`${ui.th} text-right`}>Recommended</th>
                <th className={`${ui.th} text-right`}>Worked FTE</th>
                <th className={`${ui.th} text-right`}>vs Baseline</th>
              </tr>
            </thead>
            <tbody className={ui.tbody}>
              {weeks.map(w => (
                <tr key={w.WeekStartDate} className="hover:bg-surface-hover">
                  <td className={`${ui.td} whitespace-nowrap`}>{fmtWeek(w.WeekStartDate)} – {fmtWeek(w.WeekEndDate)}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>{fmtHours(w.ScheduledHours)}</td>
                  <td className={`${ui.td} text-right tabular-nums font-semibold ${workedTone(w.WorkedHours)}`}>
                    {fmtHours(w.WorkedHours)}
                  </td>
                  <td className={`${ui.td} text-right tabular-nums`}>{fmtHours(w.AnsweringRoleWorkedHours)}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>{fmtHours(w.RecommendedHours)}</td>
                  <td className={`${ui.td} text-right tabular-nums`}>{w.WorkedFTE.toFixed(2)}</td>
                  <td className={`${ui.td} text-right tabular-nums ${w.WorkedVsBaselinePct <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {fmtPct(w.WorkedVsBaselinePct)}
                  </td>
                </tr>
              ))}
              {!loading && weeks.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                    No weekly data yet — the Workforce tables are populated by the CS project loaders.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>)}
      </section>

      {/* ── Roster ───────────────────────────────────────────── */}
      <section className={ui.sectionCard}>
        <button type="button" className="mb-1 flex items-center gap-2" onClick={() => toggle('roster')}>
          <Chevron open={!collapsed.roster} />
          <h2 className={ui.sectionTitle}>Roster</h2>
        </button>
        {!collapsed.roster && (<>
        <p className="mb-4 text-sm text-muted">
          {activeRoster.length > 0
            ? `${activeRoster.length} active agent${activeRoster.length === 1 ? '' : 's'}. `
            : ''}
          Read-only — the roster is seeded and maintained by the CS project.
        </p>
        <div className={ui.tableWrap}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>Name</th>
                <th className={ui.th}>Role</th>
                <th className={ui.th}>Time Zone</th>
                <th className={ui.th}>Active</th>
              </tr>
            </thead>
            <tbody className={ui.tbody}>
              {roster.map(r => (
                <tr key={r.RosterID} className="hover:bg-surface-hover">
                  <td className={ui.td}>{r.CanonicalName}</td>
                  <td className={ui.td}>{r.Role}</td>
                  <td className={ui.td}>{r.TimeZone}</td>
                  <td className={ui.td}>{r.IsActive ? 'Yes' : <span className="text-muted">No</span>}</td>
                </tr>
              ))}
              {!loading && roster.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted">
                    No roster loaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>)}
      </section>
    </div>
  );
};
